import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Divider
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Delete as DeleteIcon,
  AccountBalance as AccountIcon,
  MoneyOff as MoneyOffIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import * as invoicePaymentService from '../../services/invoicePaymentService';
import { IAccount, IInvoicePayment, IInvoiceRemainingDetails } from '../../services/invoicePaymentService';

interface InvoicePaymentSectionProps {
  invoiceId: string;
  onPaymentComplete?: () => void;
  readOnly?: boolean;
}

const InvoicePaymentSection: React.FC<InvoicePaymentSectionProps> = ({
  invoiceId,
  onPaymentComplete,
  readOnly = false
}) => {
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<IAccount[]>([]);
  const [invoiceDetails, setInvoiceDetails] = useState<IInvoiceRemainingDetails | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [paymentAmount, setPaymentAmount] = useState<number>(0);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [paymentToCancel, setPaymentToCancel] = useState<IInvoicePayment | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  // جلب البيانات الأولية
  useEffect(() => {
    fetchInitialData();
  }, [invoiceId]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [accountsData, detailsData] = await Promise.all([
        invoicePaymentService.getAvailableAccounts(),
        invoicePaymentService.getInvoiceRemainingDetails(invoiceId)
      ]);

      setAccounts(accountsData);
      setInvoiceDetails(detailsData);
      
      // تعيين المبلغ الافتراضي للمبلغ المتبقي
      setPaymentAmount(detailsData.remainingAmount);

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // حساب المبلغ المتبقي محلياً لضمان الدقة
  const calculateRemainingAmount = () => {
    if (!invoiceDetails) return 0;
    
    // حساب إجمالي السدادات النشطة
    const totalActivePaid = invoiceDetails.payments
      .filter(payment => payment.status === 'active')
      .reduce((total, payment) => total + payment.amount, 0);
    
    // إضافة الدفع النقدي الجزئي إذا كان موجوداً
    const cashPaidAmount = invoiceDetails.invoice.cashPaidAmount || 0;
    
    // المبلغ المتبقي = إجمالي الفاتورة - السدادات النشطة - الدفع النقدي الجزئي
    const remaining = invoiceDetails.invoice.total - totalActivePaid - cashPaidAmount;
    
    console.log(`حساب المبلغ المتبقي للفاتورة ${invoiceDetails.invoice.invoiceNumber}:`, {
      total: invoiceDetails.invoice.total,
      subsequentPayments: totalActivePaid,
      cashPaid: cashPaidAmount,
      remaining: Math.max(0, remaining)
    });
    
    return Math.max(0, remaining);
  };

  const remainingAmount = calculateRemainingAmount();

  // تحديث المبلغ المتبقي تلقائياً عند تغيير بيانات الفاتورة
  useEffect(() => {
    if (invoiceDetails && remainingAmount >= 0) {
      setPaymentAmount(remainingAmount);
    }
  }, [invoiceDetails, remainingAmount]);

  const handlePayment = async () => {
    if (!selectedAccountId || paymentAmount <= 0) {
      setError('يرجى اختيار الحساب وإدخال مبلغ صحيح');
      return;
    }

    if (!invoiceDetails || paymentAmount > remainingAmount) {
      setError('المبلغ المدخل أكبر من المبلغ المتبقي');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      await invoicePaymentService.payRemainingAmount(invoiceId, {
        accountId: selectedAccountId,
        amount: paymentAmount,
        description: description || `تسديد متبقي فاتورة ${invoiceDetails.invoice.invoiceNumber}`
      });

      setSuccess('تم تسديد المبلغ بنجاح');
      
      // إعادة تحميل البيانات
      await fetchInitialData();
      
      // إعادة تعيين النموذج
      setSelectedAccountId('');
      setDescription('');

      // إشعار المكون الأب
      if (onPaymentComplete) {
        onPaymentComplete();
      }

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelPayment = async () => {
    if (!paymentToCancel) return;

    try {
      setLoading(true);
      setError(null);

      await invoicePaymentService.cancelPayment(paymentToCancel._id);
      
      setSuccess('تم إلغاء السداد بنجاح');
      setCancelDialogOpen(false);
      setPaymentToCancel(null);
      
      // إعادة تحميل البيانات
      await fetchInitialData();

      if (onPaymentComplete) {
        onPaymentComplete();
      }

    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && !invoiceDetails) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (!invoiceDetails) {
    return null;
  }


  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: '#1976d2', fontWeight: 'bold' }}>
        <Box display="flex" alignItems="center" gap={1}>
          <PaymentIcon />
          {remainingAmount <= 0 ? 'إدارة السدادات' : 'تسديد المبلغ المتبقي'}
        </Box>
      </Typography>

      {/* رسالة اكتمال التسديد */}
      {remainingAmount <= 0 && (
        <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3, p: 2, bgcolor: '#e8f5e9', borderRadius: 2 }}>
          <CheckCircleIcon color="success" />
          <Typography variant="h6" color="success.main">
            تم تسديد الفاتورة بالكامل
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* معلومات الفاتورة */}
      <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          <strong>فاتورة رقم:</strong> {invoiceDetails.invoice.invoiceNumber}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>إجمالي الفاتورة:</strong> {invoiceDetails.invoice.total.toFixed(2)} ريال
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <strong>المدفوع من الحسابات:</strong> {invoiceDetails.totalPaidFromAccounts.toFixed(2)} ريال
        </Typography>
        <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
          <strong>المبلغ المتبقي:</strong> {remainingAmount.toFixed(2)} ريال
        </Typography>
      </Box>

      {/* نموذج التسديد - يظهر فقط للفواتير المعلقة وعندما يكون هناك مبلغ متبقي */}
      {!readOnly && remainingAmount > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            تسديد جديد
          </Typography>
          
          <Box display="flex" flexDirection="column" gap={2}>
            <FormControl fullWidth size="small">
              <InputLabel>اختر الحساب للتسديد</InputLabel>
              <Select
                value={selectedAccountId}
                onChange={(e) => setSelectedAccountId(e.target.value)}
                label="اختر الحساب للتسديد"
              >
                {accounts.map((account) => (
                  <MenuItem key={account._id} value={account._id}>
                    <Box display="flex" justifyContent="space-between" width="100%">
                      <span>{account.name}</span>
                      <span style={{ color: '#666' }}>
                        {account.accountNumber || 'غير محدد'}
                      </span>
                    </Box>
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <TextField
            label="المبلغ المراد تسديده"
            type="number"
            value={paymentAmount}
            onChange={(e) => setPaymentAmount(parseFloat(e.target.value) || 0)}
            size="small"
            InputProps={{
              inputProps: {
                min: 0,
                max: remainingAmount,
                step: 0.01
              },
              endAdornment: <Typography variant="body2" sx={{ ml: 1 }}>ريال</Typography>
            }}
            helperText={`الحد الأقصى: ${remainingAmount.toFixed(2)} ريال`}
          />

          <TextField
            label="ملاحظات (اختياري)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            size="small"
            multiline
            rows={2}
          />

            <Button
              variant="contained"
              onClick={handlePayment}
              disabled={loading || !selectedAccountId || paymentAmount <= 0 || paymentAmount > remainingAmount}
              startIcon={loading ? <CircularProgress size={20} /> : <PaymentIcon />}
              sx={{ alignSelf: 'flex-start' }}
            >
              {loading ? 'جاري التسديد...' : 'تسديد المبلغ'}
            </Button>
          </Box>
        </Box>
      )}

      {/* قائمة السدادات السابقة */}
      {invoiceDetails.payments.length > 0 && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle2" gutterBottom>
            السدادات السابقة
          </Typography>
          
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>التاريخ</TableCell>
                  <TableCell>الحساب</TableCell>
                  <TableCell align="right">المبلغ</TableCell>
                  <TableCell>الحالة</TableCell>
                  <TableCell align="center">الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoiceDetails.payments.map((payment) => (
                  <TableRow key={payment._id}>
                    <TableCell>
                      {formatDate(payment.paymentDate)}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {payment.accountName}
                        </Typography>
                        {payment.accountNumber && (
                          <Typography variant="caption" color="text.secondary">
                            {payment.accountNumber}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      {payment.amount.toFixed(2)} ريال
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={payment.status === 'active' ? 'نشط' : 'ملغي'}
                        color={payment.status === 'active' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      {payment.status === 'active' && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setPaymentToCancel(payment);
                            setCancelDialogOpen(true);
                          }}
                          disabled={loading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* مربع حوار تأكيد الإلغاء */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>تأكيد إلغاء السداد</DialogTitle>
        <DialogContent>
          <Typography>
            هل أنت متأكد من إلغاء سداد بمبلغ {paymentToCancel?.amount.toFixed(2)} ريال؟
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            سيتم إرجاع المبلغ إلى الحساب وتحديث حالة الفاتورة.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>
            إلغاء
          </Button>
          <Button 
            onClick={handleCancelPayment} 
            color="error" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : <MoneyOffIcon />}
          >
            {loading ? 'جاري الإلغاء...' : 'تأكيد الإلغاء'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default InvoicePaymentSection;